import { He6UltraDeviceList } from '@/define/device_vendor'
import { deviceManager } from './device_manager'

export abstract class DeviceProtocolBase {
  abstract connect(): Promise<boolean>
  // send/sendFeature 必须返回 Promise，上层 processQueue 需要 await 真正的 HID 发送完成，
  // 否则 HID 报错（InvalidStateError 等）会被吞掉，调度器会错误地进入“等回包 → 超时”。
  abstract send(data: number[]): Promise<void>
  abstract sendFeature(data: number[]): Promise<void>
  abstract onReceive(fn: (data: number[]) => void): void
  // 允许上层在切换/断开时释放底层句柄（如 oninputreport），避免旧 HIDDevice 实例被复用时产生 cross-talk。
  dispose?(): void
}

// 如果返回 true，表示该帧是设备主动推送的通知，不是当前命令的回包，调度器不应 resolve task。
export type ResponseFilter = (data: number[]) => boolean

interface QueuePromiss {
  resolve: (value: number[] | PromiseLike<number[]>) => void
  reject: (reason?: any) => void
  arguments: number[]
  isSendFeature: boolean
  haveResult: boolean
  timeoutMs: number
}

// 单次设备响应等待上限。设备无回包时（固件 bug、不支持的指令、HID 异常）
// 必须能自动放行队列，否则后续所有 send 都会被永久挂起。
// 默认 2s 对普通读/写足够；固件升级、校准读取、批量写入等需要更长超时的调用点可以通过
// send() 的第四参数单独传入。
const DEFAULT_RESPONSE_TIMEOUT_MS = 5000

export class DeviceProtocol {
  public deviceHID: DeviceProtocolBase | null = null

  // 当前正在等待回包的 task 上下文。严格仅在 processQueue 内部赋值，避免跨 task 串扰。
  private currentResolve: ((value: number[]) => void) | null = null
  private currentReject: ((reason?: any) => void) | null = null

  private queue: QueuePromiss[] = []
  private processing = false

  private reciveAnyHandlers: Array<(data: number[]) => void> = []
  private responseFilters: ResponseFilter[] = []

  public onReciveAny(fn: (data: number[]) => void) {
    this.reciveAnyHandlers.push(fn)
    return () => {
      this.reciveAnyHandlers = this.reciveAnyHandlers.filter((handler) => handler !== fn)
    }
  }

  // DeviceProtocol 是单例，跨设备/重连复用，需要驱动 destroy 时清空残留 listener，避免内存泄漏与重复回调。
  public clearReciveAnyHandlers() {
    this.reciveAnyHandlers = []
  }

  // 设备驱动可注册“哪些帧不属于命令回包”的判断器（如 He6Ultra 的 0x0a 状态帧）。
  // 返回一个取消函数，驱动 destroy 时调用，避免跨设备污染。
  public registerResponseFilter(filter: ResponseFilter): () => void {
    this.responseFilters.push(filter)
    return () => {
      this.responseFilters = this.responseFilters.filter((f) => f !== filter)
    }
  }

  public clearResponseFilters() {
    this.responseFilters = []
  }

  constructor() {
    // this.deviceModel = new he3Model(this);
  }

  private async processQueue(): Promise<void> {
    if (this.processing) return
    this.processing = true

    // 必须用 try/finally 保证 processing 复位。否则任何未处理的异常（如底层 HID throw）
    // 都会让 processing 永远处于 true，后续所有 send() 进队后都不会被调度 —— 完全卡死。
    try {
      while (this.queue.length > 0) {
        const task = this.queue.shift()
        if (!task) continue
        if (!this.deviceHID) {
          task.reject('device not connected')
          continue
        }

        // task-local resolver：跨 task 不再共享同一个类成员变量，超时后的延迟回包不会误伤下一个 task。
        let answerPromise: Promise<number[]> | null = null
        if (task.haveResult) {
          answerPromise = new Promise<number[]>((resolve, reject) => {
            this.currentResolve = resolve
            this.currentReject = reject
          })
        }

        try {
          if (task.isSendFeature) {
            await this.deviceHID.sendFeature(Array.from(task.arguments))
          } else {
            await this.deviceHID.send(Array.from(task.arguments))
          }
        } catch (e) {
          // HID 底层发送失败（如 InvalidStateError）。清理 task-local resolver 后直接出错，
          // 不能进入后续的“等回包 → 超时”分支，避免掩盖真实错误。
          this.currentResolve = null
          this.currentReject = null
          task.reject(e)
          continue
        }

        if (!task.haveResult || !answerPromise) {
          task.resolve([])
          continue
        }

        let timeoutId: ReturnType<typeof setTimeout> | null = null
        const timeoutPromise = new Promise<number[]>((_, reject) => {
          timeoutId = setTimeout(() => {
            console.log('device response timeout:', task.arguments, task.timeoutMs)
            reject(new Error('device response timeout'))
          }, task.timeoutMs)
        })
        try {
          const res = await Promise.race([answerPromise, timeoutPromise])
          task.resolve(res)
        } catch (e) {
          task.reject(e)
        } finally {
          if (timeoutId !== null) clearTimeout(timeoutId)
          // 无论超时、成功还是外部被 reject，都要解除 task-local resolver 引用，
          // 避免后续的设备帧误传到已完成的 task 上。
          this.currentResolve = null
          this.currentReject = null
        }
      }
    } finally {
      this.processing = false
    }
  }

  send(data: number[], isSendFeature = false, haveResult = true, timeoutMs = DEFAULT_RESPONSE_TIMEOUT_MS): Promise<number[]> {
    return new Promise((resolve, reject) => {
      this.queue.push({
        resolve,
        reject,
        arguments: data,
        isSendFeature,
        haveResult,
        timeoutMs,
      })
      this.processQueue()
    })
  }

  hasPendingResponseTraffic() {
    return this.currentResolve !== null || this.queue.some((task) => task.haveResult)
  }

  hasPendingTraffic() {
    return this.hasPendingResponseTraffic()
  }

  setBase = async (deviceHID: DeviceProtocolBase) => {
    // 切换设备前清理旧连接的残留状态：未消费的队列、悬挂的 resolver、
    // 旧设备的 reciveAny 监听都不能带到新设备上，否则会出现“切回选择页重选设备
    // 仍然卡死”的现象。
    this.disconnect()
    this.deviceHID = deviceHID
    await this.deviceHID.connect()
    this.deviceHID.onReceive((data) => {
      // 先走设备驱动注册的过滤器：它们判定该帧是不是主动推送。
      // 同时保留历史逻辑对 He6Ultra 0x0a 的兼容，避免驱动还未迁移完成时回归。
      const isPushFrame = this.responseFilters.some((filter) => filter(data)) || (He6UltraDeviceList.includes(deviceManager.deviceID) && data[0] == 0x0a)
      if (!isPushFrame && this.currentResolve) {
        const resolve = this.currentResolve
        // 立刻清空引用：同一 task 不能被多个帧重复 resolve，后续帧全部当 push frame 处理。
        this.currentResolve = null
        this.currentReject = null
        resolve(data)
      }
      this.reciveAnyHandlers.forEach((handler) => {
        try {
          handler(data)
        } catch (err) {
          // 单个 handler 报错不能阐断后续 handler，也不应冒泡到 HID 回调。
          console.error('reciveAny handler error:', err)
        }
      })
    })
  }

  disconnect = () => {
    // 注意：processing 不要手动置为 false，processQueue 的 try/finally 会在当前 task settle 后自行复位。
    // 如果在这里提前清零，下一个 setBase 后立即 send 可能与还在走的旧循环重入。
    this.queue.forEach((task) => task.reject(new Error('device disconnected')))
    this.queue = []
    if (this.currentReject) {
      const reject = this.currentReject
      this.currentResolve = null
      this.currentReject = null
      reject(new Error('device disconnected'))
    }
    // 释放旧底层句柄（如 DeviceWeb 上的 oninputreport），避免 HIDDevice 实例被复用时
    // 旧回调仍然 fire。
    this.deviceHID?.dispose?.()
    this.deviceHID = null
  }
}

export const deviceProtocol = new DeviceProtocol()
